import apiRoutes from "@/src/apiRoutes";
import api, { formatNumber, removeUndefinedAndNull } from "@/src/utils";
import { useMutation, useQuery } from "@tanstack/react-query";
import { textVariants } from "@/src/components/Text/Text";
import { Avatar } from "@mantine/core";
import { buttonVariants } from "@/src/components/Button/Button";
import { cn } from "@/lib/utils";
import { Link, useNavigate } from "react-router-dom";
import Loader from "@/src/components/Loader";
import useUrlState from "@/src/hooks/use-url-state";
import useDebounce from "@/src/hooks/useDebounce";
import { OffersData } from "../P2PTrade/types";
import { isEmpty } from "lodash";
import empty from "@assets/search file.svg";
import { toast } from "sonner";
import { Affix, Tooltip, rem } from "@mantine/core";
import { MoreHorizontal, PenBox, PlusIcon, Trash2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/src/components/shad/dropdown";
import { useTranslation } from "react-i18next";
import { TOptions } from "i18next";

const Offers = () => {
  const { t } = useTranslation();
  const trans = (key: string, options?: TOptions) =>
    t(`app.p2p.myAds.${key}`, options);
  const navigate = useNavigate();
  const [Coin] = useUrlState("coin");
  const [Type] = useUrlState("type");
  const [Currency] = useUrlState("currency");
  const [minAmount] = useUrlState("min_amount");
  const debAmount = useDebounce(minAmount, 1500);

  const fetchOffers = async () => {
    const res = await api.post(
      apiRoutes.myoffers,
      Type || Currency || Coin
        ? {
            type: "fetch",
            filters: removeUndefinedAndNull({
              coin__id: Coin ?? undefined,
              offer_type: Type ?? undefined,
              currency: Currency ?? undefined,
              trading_amount__gte: Number(debAmount) ?? undefined,
            }),
          }
        : {
            type: "fetch",
            filters: {},
          }
    );

    if (!res.data.success) {
      toast.error(res.data?.info);
      throw new Error(res.data.info);
    }
    return res.data;
  };

  const { data, isLoading, isFetching, refetch } = useQuery<OffersData>({
    queryKey: ["fetch_my_offers", Type, Coin, Currency, debAmount],
    queryFn: fetchOffers,
    // onSuccess: (data) => {
    //     // if (!data?.success) {
    //     //     throw new Error('error')
    //     // }
    //     // return data
    // },
    retry: 0,
  });

  const deleteOffer = async (id: string) => {
    const res = await api.post(apiRoutes.myoffers, {
      type: "delete",
      id,
    });
    return res.data;
  };
  const deleteMutation = useMutation({
    mutationFn: deleteOffer,
    onSuccess: (data) => {
      if (data?.success) {
        refetch();
        toast.success(data?.info);
      }
    },
  });

  if (isLoading || isFetching) return <Loader />;

  if (isEmpty(data?.info))
    return (
      <div className="p-6">
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="flex flex-col items-center">
            <img src={empty} alt="no  offer" className="h-20 w-full" />
            <div className="mt-4">
              <span className={cn(textVariants({}), "text-center")}>
                {trans("empty.notFound")}
              </span>
              <Link
                to={"/app/trade/create"}
                className={cn(buttonVariants(), "px-6")}
              >
                {trans("empty.createNow")}
              </Link>
            </div>
          </div>
        </div>
      </div>
    );

  return (
    <div className="md:mt-5 divide-y divide-gray-100 dark:divide-grey-500">
      <div className="md:grid hidden  grid-cols-5 p-2 font-semibold bg-white dark:bg-grey-500">
        <span className={textVariants({ variant: "desc" })}>
          {trans("header.advertiser")}
        </span>
        <span className={textVariants({ variant: "desc" })}>
          {trans("header.rate")}
        </span>
        <span className={textVariants({ variant: "desc" })}>
          {trans("header.quantity")}
        </span>
        <span className={textVariants({ variant: "desc" })}>
          {trans("header.paymentMethod")}
        </span>
        <span className={textVariants({ variant: "desc" })}>
          {trans("header.tradeAction")}
        </span>
      </div>
      <div className="divide-y divide-gray-100 dark:divide-grey-500 md:grid hidden ">
        {data?.info?.map((offer) => (
          // Desktop view
          <div key={offer?.uuid}>
            <div className="grid grid-cols-5 py-3 px-2">
              <div className="flex gap-2 ">
                <div>
                  <Avatar size={"sm"} color="blue" radius={"xl"}>
                    {offer?.trader__username[0]}{" "}
                  </Avatar>
                </div>
                <div className="grid">
                  <span
                    onClick={() =>
                      navigate(
                        `/app/trade/trader_info/${offer?.trader__username}`
                      )
                    }
                    className={cn(
                      textVariants({ size: "sm", case: "capitalize" }),
                      "cursor-pointer"
                    )}
                  >
                    {" "}
                    {offer?.trader__username}
                  </span>
                  <span
                    className={textVariants({
                      size: "xs",
                      variant: "desc",
                      font: "semibold",
                    })}
                  >
                    {trans("body.transactions", {
                      total: offer?.transactions ?? 0,
                    })}
                  </span>
                </div>
              </div>
              <div className="grid">
                <span className={textVariants()}>
                  {offer?.currency} {offer?.unit_price}
                </span>
              </div>
              <span className="flex flex-col">
                <span className={textVariants({ size: "sm", variant: "desc" })}>
                  {trans("body.quantity")}
                  <span className={textVariants({ size: "sm" })}>
                    {" "}
                    {offer?.trading_amount}{" "}
                    <span className="uppercase">{offer?.currency}</span>
                  </span>
                </span>
                <span className={textVariants({ size: "sm", variant: "desc" })}>
                  {trans("body.limits")}
                  <span className={textVariants({ size: "sm" })}>
                    {" "}
                    {offer?.min_order_price} - {offer?.max_order_price}
                  </span>
                </span>
              </span>
              <span>
                {offer?.payment_method.map((item) => (
                  <div className="flex items-center gap-1" key={item?.id}>
                    <img
                      src={item?.image}
                      alt={item?.name}
                      className="h-4 w-4"
                    />
                    <span className="pl-1">{item?.name}</span>{" "}
                  </div>
                ))}
              </span>
              <span className="flex items-center">
                <DropdownMenu>
                  <DropdownMenuTrigger>
                    <MoreHorizontal className="h-6 w-6 dark:text-gray-100 text-gray-500" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="bg-white dark:bg-grey-700">
                    <DropdownMenuItem>
                      <Link
                        reloadDocument
                        // to={`edit/${data?.info}`}
                        to={`/app/trade/update/${offer?.uuid}`}
                        className="flex gap-2"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="lucide lucide-square-pen h-4 w-4"
                        >
                          <path d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                          <path d="M18.375 2.625a1 1 0 0 1 3 3l-9.013 9.014a2 2 0 0 1-.853.505l-2.873.84a.5.5 0 0 1-.62-.62l.84-2.873a2 2 0 0 1 .506-.852z" />
                        </svg>
                        {/* <img src={Edit} alt="edit icon" /> */}
                        <span
                          className={textVariants({
                            size: "xs",
                          })}
                        >
                          {trans("actions.edit")}
                        </span>
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem>
                      <button
                        onClick={() =>
                          deleteMutation.mutate(offer?.id.toString())
                        }
                        className={cn(
                          "flex items-center gap-1 ",
                          deleteMutation.isLoading && "cursor-wait"
                        )}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                        <span
                          className={textVariants({
                            size: "xs",
                          })}
                        >
                          {trans("actions.delete")}
                        </span>
                      </button>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </span>
            </div>
          </div>
        ))}
      </div>
      <div className="md:hidden grid gap-3">
        {data?.info.map((offer) => (
          <div className="pt-3" key={offer?.uuid}>
            <div className="flex items-center justify-between">
              <div className="grid gap-0.5 ">
                <div className={cn("flex items-center gap-2 mb-2")}>
                  <div>
                    <Avatar size={"sm"} color="blue" radius={"xl"}>
                      {offer?.trader__username[0]}{" "}
                    </Avatar>
                  </div>
                  <div className={cn(textVariants({ font: "medium" }))}>
                    {offer?.trader__username}
                  </div>
                </div>
                <span
                  className={cn(
                    textVariants({ size: "xs", variant: "desc" }),
                    "-mt-1.5"
                  )}
                >
                  {/* {offer?.transactions ?? 0} transactions | {offer?.completed} */}
                  {trans("body.transactions", {
                    total: offer?.transactions ?? 0,
                  })}
                </span>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger>
                  <MoreHorizontal className="h-6 w-6 dark:text-gray-100 text-gray-500" />
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-white dark:bg-grey-700">
                  <DropdownMenuItem>
                    <Link
                      reloadDocument
                      // to={`edit/${data?.info}`}
                      to={`/app/trade/update/${offer?.uuid}`}
                      className="flex gap-2 py-2"
                    >
                      <PenBox className="h-4 w-4 text-gray-500" />
                      <span
                        className={textVariants({
                          size: "xs",
                        })}
                      >
                        {trans("actions.edit")}
                      </span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem>
                    <button
                      onClick={() =>
                        deleteMutation.mutate(offer?.id.toString())
                      }
                      className={cn(
                        "flex items-center gap-1 py-2 ",
                        deleteMutation.isLoading && "cursor-wait"
                      )}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                      <span
                        className={textVariants({
                          size: "xs",
                        })}
                      >
                        {trans("actions.delete")}
                      </span>
                    </button>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <div className="flex items-center justify-between mt-2">
              <div className="grid gap-1">
                <div className="flex gap-2 items-center">
                  <span
                    className={cn(
                      textVariants({ font: "semibold", size: "xs" }),
                      "mt-2"
                    )}
                  >
                    {offer?.currency}
                  </span>
                  <span className={textVariants({ size: "xl", font: "bold" })}>
                    {formatNumber(offer?.unit_price)}
                  </span>
                </div>
                <span className={textVariants({ size: "xs", variant: "desc" })}>
                  {trans("body.quantity")}:
                  <span
                    className={textVariants({ size: "xs", case: "uppercase" })}
                  >
                    {" "}
                    {formatNumber(offer?.trading_amount)} {offer?.currency}
                  </span>
                </span>
                <span className={textVariants({ size: "xs", variant: "desc" })}>
                  {trans("body.limits")}:
                  <span className={textVariants({ size: "xs" })}>
                    {" "}
                    {formatNumber(offer?.min_order_price)} -{" "}
                    {formatNumber(offer?.max_order_price)} {""}
                    {offer?.currency}
                  </span>
                </span>
              </div>

              <div className="flex  ">
                <div>
                  <span
                    className={cn(
                      textVariants({ size: "xs", case: "capitalize" }),
                      "bg-blue-700 text-white px-2 py-1 rounded-md"
                    )}
                  >
                    {offer?.offer_type?.toLowerCase() === "buy"
                      ? trans("body.buyAd")
                      : trans("body.sellAd")}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex gap-2 mt-3">
              {offer?.payment_method.map((item) => (
                <div className="flex items-center gap-1" key={item?.id}>
                  <img
                    className="h-4 w-4 rounded-full"
                    src={item?.image}
                    alt={item?.name}
                  />
                  <span className={cn(textVariants({ size: "xs" }))}>
                    {item?.name}
                  </span>{" "}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      {!isEmpty(data?.info) && (
        <Affix position={{ bottom: rem(40), right: rem(40) }}>
          <Tooltip label="Create Offer">
            <Link to={"/app/trade/create"}>
              <div className="bg-indigo-500 cursor-pointer rounded-full p-4 text-white text-xl">
                <PlusIcon className="h-6 w-6 text-white" />
              </div>
            </Link>
          </Tooltip>
        </Affix>
      )}
    </div>
  );
};

export default Offers;
